import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

const SESSIONS_COLLECTION = 'sessions';

const TOKEN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export const normalizeSessionToken = (value) =>
  String(value ?? '').trim().toUpperCase();

export const generateSessionToken = (length = 12) => {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += TOKEN_ALPHABET[Math.floor(Math.random() * TOKEN_ALPHABET.length)];
  }
  return result;
};

const toScenesMeta = (scenes) => {
  if (!Array.isArray(scenes)) return null;
  return scenes
    .filter((scene) => scene && typeof scene === 'object')
    .map((scene) => ({
      id: scene.id ?? null,
      name: scene.name ?? '',
    }))
    .filter((scene) => !!scene.id);
};

export const createSession = async (
  db,
  { createdBy, scenes = null, scenesMeta = null, currentSceneId = null } = {}
) => {
  // Try a few times in case of collisions.
  for (let attempt = 0; attempt < 6; attempt++) {
    const token = generateSessionToken(12);
    const ref = doc(db, SESSIONS_COLLECTION, token);
    const existing = await getDoc(ref);
    if (existing.exists()) continue;

    await setDoc(ref, {
      token,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: createdBy ?? null,
      // Keep sessions lightweight: never store full scene payloads (can include large image data).
      scenesMeta: toScenesMeta(scenesMeta ?? scenes),
      currentSceneId: currentSceneId ?? null,
      active: true,
    });

    return token;
  }

  throw new Error('Failed generating a unique session token.');
};

export const getSession = async (db, token) => {
  const normalized = normalizeSessionToken(token);
  if (!normalized) return null;
  const snapshot = await getDoc(doc(db, SESSIONS_COLLECTION, normalized));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() };
};

export const updateSessionSnapshot = async (
  db,
  token,
  { scenes = undefined, scenesMeta = undefined, currentSceneId = undefined } = {}
) => {
  const normalized = normalizeSessionToken(token);
  if (!normalized) throw new Error('Missing session token');

  const patch = {
    updatedAt: serverTimestamp(),
  };

  if (scenesMeta !== undefined || scenes !== undefined) {
    patch.scenesMeta = toScenesMeta(scenesMeta ?? scenes);
  }
  if (currentSceneId !== undefined) {
    patch.currentSceneId = currentSceneId ?? null;
  }

  return updateDoc(doc(db, SESSIONS_COLLECTION, normalized), patch);
};

// Extend update to optionally include full scene payload and allowPlayerControls flag
export const updateSessionDoc = async (
  db,
  token,
  { scene = undefined, allowPlayerControls = undefined, isOnBreak = undefined, scenes = undefined, scenesMeta = undefined, currentSceneId = undefined, gameplaySyncEnabled = undefined, worldMap = undefined, library = undefined, worldMapShared = undefined, libraryShared = undefined, pings = undefined } = {}
) => {
  const normalized = normalizeSessionToken(token);
  if (!normalized) throw new Error('Missing session token');

  const patch = {
    updatedAt: serverTimestamp(),
  };

  if (scene !== undefined) patch.scene = scene === null ? null : scene;
  if (allowPlayerControls !== undefined) patch.allowPlayerControls = !!allowPlayerControls;
  if (isOnBreak !== undefined) patch.isOnBreak = !!isOnBreak;
  if (gameplaySyncEnabled !== undefined) patch.gameplaySyncEnabled = !!gameplaySyncEnabled;
  if (scenesMeta !== undefined || scenes !== undefined) {
    patch.scenesMeta = toScenesMeta(scenesMeta ?? scenes);
  }
  if (currentSceneId !== undefined) {
    patch.currentSceneId = currentSceneId ?? null;
  }
  if (worldMap !== undefined) patch.worldMap = worldMap === null ? null : worldMap;
  if (library !== undefined) patch.library = library === null ? null : library;
  if (worldMapShared !== undefined) patch.worldMapShared = !!worldMapShared;
  if (libraryShared !== undefined) patch.libraryShared = !!libraryShared;
  if (pings !== undefined) {
    if (!Array.isArray(pings)) throw new Error('Session pings must be an array');
    patch.pings = pings;
  }

  return updateDoc(doc(db, SESSIONS_COLLECTION, normalized), patch);
};

export const submitSessionCommand = async (db, token, { username, displayName, command }) => {
  const normalized = normalizeSessionToken(token);
  if (!normalized) throw new Error('Missing session token');
  const commandsRef = collection(db, SESSIONS_COLLECTION, normalized, 'commands');
  return addDoc(commandsRef, {
    username: username ?? 'anonymous',
    displayName: displayName ?? '',
    command: String(command ?? '').trim(),
    processed: false,
    processedAt: null,
    createdAt: serverTimestamp(),
    clientCreatedAt: Date.now(),
  });
};

export const writeSessionRoll = async (db, token, roll) => {
  const normalized = normalizeSessionToken(token);
  if (!normalized) throw new Error('Missing session token');
  const rollsRef = collection(db, SESSIONS_COLLECTION, normalized, 'rolls');
  return addDoc(rollsRef, {
    ...roll,
    createdAt: serverTimestamp(),
    clientCreatedAt: Date.now(),
  });
};

export const markCommandProcessed = async (db, token, commandId, updates = {}) => {
  const normalized = normalizeSessionToken(token);
  const ref = doc(db, SESSIONS_COLLECTION, normalized, 'commands', commandId);
  return updateDoc(ref, {
    processed: true,
    processedAt: serverTimestamp(),
    ...updates,
  });
};

export const listenSessionCommands = (db, token, onChange) => {
  const normalized = normalizeSessionToken(token);
  const commandsRef = collection(db, SESSIONS_COLLECTION, normalized, 'commands');

  // No composite indexes required: we listen to the whole subcollection and filter client-side.
  return onSnapshot(commandsRef, (snap) => {
    const docs = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.clientCreatedAt ?? 0) - (b.clientCreatedAt ?? 0));
    onChange(docs);
  });
};

export const listenSessionRolls = (db, token, onChange) => {
  const normalized = normalizeSessionToken(token);
  const rollsRef = collection(db, SESSIONS_COLLECTION, normalized, 'rolls');
  return onSnapshot(rollsRef, (snap) => {
    const docs = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.clientCreatedAt ?? 0) - (b.clientCreatedAt ?? 0));
    onChange(docs);
  });
};
